<template>
    <div class="min-h-screen bg-white">
        <!-- Translation Loading Overlay -->
        <div v-if="translating" class="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
            <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>

        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div class="mb-8">
                <h1 class="text-4xl md:text-5xl font-marketing font-bold text-primary mb-2">
                    Account Settings
                </h1>
                <p class="text-gray-600 font-sans">Manage your account settings</p>
            </div>

            <div v-if="loading" class="text-center py-12">
                <p class="text-gray-600 font-sans">Loading...</p>
            </div>

            <div v-else class="space-y-8">
                <section class="bg-creme-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Personal Information</h2>
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">First Name <span
                                    class="text-red-600">*</span></label>
                            <input v-model="firstName" type="text" placeholder="Enter your first name" required
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">Last Name <span
                                    class="text-red-600">*</span></label>
                            <input v-model="lastName" type="text" placeholder="Enter your last name" required
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">Phone Number</label>
                            <input v-model="phoneNumber" type="tel" placeholder="(123) 456-7890"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <button @click="saveName" :disabled="savingName"
                            class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ savingName ? 'Saving...' : 'Save Information' }}
                        </button>
                        <p v-if="nameMessage" :class="[
                            'text-sm font-sans',
                            nameMessage.includes('success') ? 'text-green-600' : 'text-red-600'
                        ]">
                            {{ nameMessage }}
                        </p>
                    </div>
                </section>

                <section class="bg-creme-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Change Email</h2>
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">Current Email</label>
                            <input :value="currentUser?.email" type="email" disabled
                                class="w-full px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded-md font-sans cursor-not-allowed" />
                        </div>
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">New Email</label>
                            <input v-model="newEmail" type="email" placeholder="Enter new email address"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <button @click="requestEmailChange" :disabled="savingEmail || !newEmail"
                            class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ savingEmail ? 'Sending...' : 'Request Email Change' }}
                        </button>
                        <p v-if="emailMessage" :class="[
                            'text-sm font-sans',
                            emailMessage.includes('success') || emailMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
                        ]">
                            {{ emailMessage }}
                        </p>
                        <p v-if="emailMessage && emailMessage.includes('sent')" class="text-sm text-gray-600 font-sans">
                            Please check your new email inbox to verify the change. Your email will be updated after
                            verification.
                        </p>
                    </div>
                </section>

                <section class="bg-creme-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Change Password</h2>
                    <div class="space-y-4">
                        <button @click="showPasswordModal = true"
                            class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold">
                            Change Password
                        </button>
                    </div>
                </section>

                <section class="bg-creme-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Cookie Preferences</h2>
                    <div class="space-y-4">
                        <p class="text-sm font-sans text-gray-700 mb-4">
                            Manage your cookie preferences. Essential cookies are required for the shopping cart and
                            site functionality and cannot be disabled.
                        </p>

                        <div class="space-y-3">
                            <div class="flex items-start p-4 bg-white rounded-md border border-gray-200">
                                <div class="flex-1">
                                    <div class="flex items-center justify-between">
                                        <h3 class="font-marketing font-semibold text-primary">Essential Cookies</h3>
                                        <span
                                            class="text-xs font-sans text-gray-500 bg-gray-100 px-2 py-1 rounded">Required</span>
                                    </div>
                                    <p class="text-sm font-sans text-gray-600 mt-1">
                                        These cookies are necessary for the website to function properly, including
                                        shopping cart functionality and secure access to your account.
                                    </p>
                                </div>
                            </div>

                            <div class="flex items-start p-4 bg-white rounded-md border border-gray-200">
                                <div class="flex items-center mr-3 mt-1">
                                    <input v-model="localCookiePreferences.all" type="checkbox"
                                        class="w-5 h-5 text-accent border-gray-300 rounded focus:ring-accent focus:ring-2" />
                                </div>
                                <div class="flex-1">
                                    <h3 class="font-marketing font-semibold text-primary">All Cookies</h3>
                                    <p class="text-sm font-sans text-gray-600 mt-1">
                                        Accept all cookies to help us improve your experience and provide personalized
                                        content.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button @click="saveCookiePreferences" :disabled="savingCookies"
                            class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ savingCookies ? 'Saving...' : 'Save Preferences' }}
                        </button>
                        <p v-if="cookieMessage" :class="[
                            'text-sm font-sans',
                            cookieMessage.includes('success') ? 'text-green-600' : 'text-red-600'
                        ]">
                            {{ cookieMessage }}
                        </p>
                    </div>
                </section>

                <section class="bg-creme-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Change Address</h2>
                    <div class="space-y-6">
                        <div>
                            <h3 class="text-xl font-marketing font-semibold text-primary mb-4">Shipping Address</h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input v-model="shippingAddress.name" type="text" placeholder="Full Name"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="shippingAddress.street" type="text" placeholder="Street Address"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm md:col-span-2" />
                                <input v-model="shippingAddress.city" type="text" placeholder="City"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="shippingAddress.state" type="text" placeholder="State"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="shippingAddress.zip" type="text" placeholder="ZIP Code"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="shippingAddress.country" type="text" placeholder="Country"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                            </div>
                        </div>

                        <div>
                            <h3 class="text-xl font-marketing font-semibold text-primary mb-4">Billing Address</h3>
                            <label class="flex items-center mb-4">
                                <input v-model="useShippingForBilling" type="checkbox" class="mr-2" />
                                <span class="text-sm font-sans text-gray-700">Same as shipping address</span>
                            </label>
                            <div v-if="!useShippingForBilling" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input v-model="billingAddress.name" type="text" placeholder="Full Name"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="billingAddress.street" type="text" placeholder="Street Address"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm md:col-span-2" />
                                <input v-model="billingAddress.city" type="text" placeholder="City"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="billingAddress.state" type="text" placeholder="State"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="billingAddress.zip" type="text" placeholder="ZIP Code"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                <input v-model="billingAddress.country" type="text" placeholder="Country"
                                    class="px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                            </div>
                        </div>

                        <button @click="saveAddresses" :disabled="savingAddresses"
                            class="bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ savingAddresses ? 'Saving...' : 'Save Addresses' }}
                        </button>
                        <p v-if="addressMessage" :class="[
                            'text-sm font-sans',
                            addressMessage.includes('success') ? 'text-green-600' : 'text-red-600'
                        ]">
                            {{ addressMessage }}
                        </p>
                    </div>
                </section>

                <!-- Danger Zone Section -->
                <section class="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                    <h2 class="text-2xl font-marketing font-bold text-red-700 mb-4">Danger Zone</h2>
                    <div class="space-y-4">
                        <div class="bg-white rounded-lg p-4 border border-red-300">
                            <h3 class="text-lg font-marketing font-semibold text-red-700 mb-2">Delete Account</h3>
                            <p class="text-sm font-sans text-gray-700 mb-4">
                                Once you delete your account, there is no going back. This action will permanently
                                delete
                                your account and remove all your data from our servers. Your purchase history will be
                                anonymized but preserved for legal and business purposes.
                            </p>
                            <button @click="openDeleteModal"
                                class="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors font-marketing font-semibold">
                                Delete My Account
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- Password Change Modal -->
        <div v-if="showPasswordModal"
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div class="mb-4">
                    <h3 class="text-xl font-marketing font-bold mb-2">
                        Change Password
                    </h3>
                    <p v-if="passwordMessage" :class="[
                        'text-sm font-sans mb-4 px-4 py-3 rounded-md',
                        passwordMessage.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    ]">
                        {{ passwordMessage }}
                    </p>
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">Current Password</label>
                            <input v-model="currentPassword" type="password" placeholder="Enter current password"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">New Password</label>
                            <input v-model="newPassword" type="password" placeholder="Enter new password"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-sans text-gray-700 mb-2">Confirm New Password</label>
                            <input v-model="confirmNewPassword" type="password" placeholder="Confirm new password"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end gap-3">
                    <button @click="closePasswordModal"
                        class="px-4 py-2 text-sm font-marketing bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button @click="handleChangePassword"
                        :disabled="changingPassword || !currentPassword || !newPassword || !confirmNewPassword"
                        class="px-4 py-2 text-sm font-marketing bg-accent text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ changingPassword ? 'Changing...' : 'Change Password' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Delete Account Confirmation Modal -->
        <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref="deleteModalRef" class="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
                <!-- Modal Translation Loading -->
                <div v-if="translatingModal"
                    class="absolute inset-0 bg-white bg-opacity-90 rounded-lg flex items-center justify-center z-10">
                    <div class="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>

                <div class="mb-4">
                    <h3 class="text-xl font-marketing font-bold text-red-700 mb-2">
                        Delete Account
                    </h3>
                    <div class="mb-4">
                        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <p class="text-sm font-sans text-red-800 font-semibold mb-2">
                                ⚠️ Warning: This action cannot be undone!
                            </p>
                            <p class="text-sm font-sans text-gray-700">
                                Deleting your account will:
                            </p>
                            <ul class="list-disc list-inside text-sm font-sans text-gray-700 mt-2 space-y-1">
                                <li>Permanently delete your account information</li>
                                <li>Remove access to your purchase history</li>
                                <li>Remove all your personal data from our systems</li>
                                <li>This action is irreversible</li>
                            </ul>
                        </div>
                        <p class="text-sm font-sans text-gray-700 mb-4">
                            To confirm deletion, please type <strong class="font-semibold text-red-700">DELETE</strong>
                            in the box below:
                        </p>
                        <input v-model="deleteConfirmation" type="text" placeholder="Type DELETE to confirm"
                            class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 font-sans" />
                    </div>
                    <p v-if="deleteMessage" :class="[
                        'text-sm font-sans mb-4 px-4 py-3 rounded-md',
                        'bg-red-50 text-red-700 border border-red-200'
                    ]">
                        {{ deleteMessage }}
                    </p>
                </div>
                <div class="flex justify-end gap-3">
                    <button @click="closeDeleteModal"
                        class="px-4 py-2 text-sm font-marketing bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button @click="confirmDeleteAccount" :disabled="deletingAccount || deleteConfirmation !== 'DELETE'"
                        class="px-4 py-2 text-sm font-marketing bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ deletingAccount ? 'Deleting...' : 'Delete Account' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { userService } from '../services/user'
import { authService } from '../services/auth'
import { useCookieConsent } from '../composables/useCookieConsent'
import { translationService } from '../services/translation'

const loading = ref(true)
const translating = ref(false)
const translatingModal = ref(false)
const currentUser = ref(null)
const firstName = ref('')
const lastName = ref('')
const phoneNumber = ref('')
const newEmail = ref('')
const savingName = ref(false)
const savingEmail = ref(false)
const savingAddresses = ref(false)
const savingCookies = ref(false)
const nameMessage = ref('')
const emailMessage = ref('')
const addressMessage = ref('')
const cookieMessage = ref('')
const useShippingForBilling = ref(false)
const showPasswordModal = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const confirmNewPassword = ref('')
const changingPassword = ref(false)
const passwordMessage = ref('')
const showDeleteModal = ref(false)
const deleteConfirmation = ref('')
const deletingAccount = ref(false)
const deleteMessage = ref('')
const deleteModalRef = ref(null)

const { cookiePreferences, updatePreferences } = useCookieConsent()
const localCookiePreferences = ref({ ...cookiePreferences.value })

const shippingAddress = ref({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: ''
})

const billingAddress = ref({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: ''
})

onMounted(async () => {
    currentUser.value = authService.getStoredUser()

    if (!currentUser.value) {
        const fetchedUser = await authService.getCurrentUser()
        if (!fetchedUser) {
            window.location.href = '/login'
            return
        }
        currentUser.value = fetchedUser
    }

    await loadUserData()
    // Load cookie preferences
    localCookiePreferences.value = { ...cookiePreferences.value }

    // Apply translations to the page
    await nextTick()
    const currentLang = translationService.getLanguage()
    if (currentLang !== 'en') {
        translating.value = true
        const root = document.querySelector('.min-h-screen')
        if (root) {
            await translationService.translateElement(root)
        }
        translating.value = false
    }

    // Listen for language changes
    window.addEventListener('language-changed', handleLanguageChange)
})

async function handleLanguageChange() {
    translating.value = true
    await nextTick()
    const currentLang = translationService.getLanguage()
    const root = document.querySelector('.min-h-screen')
    if (root) {
        if (currentLang === 'en') {
            await translationService.revertToEnglish(root)
        } else {
            await translationService.translateElement(root)
        }
    }
    translating.value = false
}

async function loadUserData() {
    loading.value = true
    try {
        const result = await userService.getCurrentUser()
        if (result.success && result.data) {
            currentUser.value = result.data
            firstName.value = result.data.first_name || ''
            lastName.value = result.data.last_name || ''
            phoneNumber.value = result.data.phone_number || ''

            if (result.data.shipping_address) {
                shippingAddress.value = {
                    name: result.data.shipping_address.name || '',
                    street: result.data.shipping_address.street || '',
                    city: result.data.shipping_address.city || '',
                    state: result.data.shipping_address.state || '',
                    zip: result.data.shipping_address.zip || '',
                    country: result.data.shipping_address.country || ''
                }
            }

            if (result.data.billing_address) {
                billingAddress.value = {
                    name: result.data.billing_address.name || '',
                    street: result.data.billing_address.street || '',
                    city: result.data.billing_address.city || '',
                    state: result.data.billing_address.state || '',
                    zip: result.data.billing_address.zip || '',
                    country: result.data.billing_address.country || ''
                }
            }
        }
    } catch (error) {
        console.error('Error loading user data:', error)
    } finally {
        loading.value = false
    }
}

async function saveName() {
    savingName.value = true
    nameMessage.value = ''

    try {
        const result = await userService.updateName(firstName.value, lastName.value, phoneNumber.value)

        if (result.success) {
            nameMessage.value = 'Information updated successfully!'
            currentUser.value = {
                ...currentUser.value,
                first_name: firstName.value,
                last_name: lastName.value,
                phone_number: phoneNumber.value
            }
            localStorage.setItem('user', JSON.stringify(currentUser.value))
            setTimeout(() => {
                nameMessage.value = ''
            }, 3000)
        } else {
            nameMessage.value = result.error || 'Failed to update name'
        }
    } catch (error) {
        nameMessage.value = error.response?.data?.error || 'Failed to update name'
        console.error('Error updating name:', error)
    } finally {
        savingName.value = false
    }
}

async function requestEmailChange() {
    if (!newEmail.value || newEmail.value === currentUser.value?.email) {
        emailMessage.value = 'Please enter a new email address'
        return
    }

    savingEmail.value = true
    emailMessage.value = ''

    try {
        const result = await userService.requestEmailChange(newEmail.value)

        if (result.success) {
            emailMessage.value = result.data?.message || 'Verification email sent to your new email address. Please check your inbox to confirm the change.'
            newEmail.value = ''
            setTimeout(() => {
                emailMessage.value = ''
            }, 5000)
        } else {
            emailMessage.value = result.error || 'Failed to request email change'
        }
    } catch (error) {
        emailMessage.value = error.response?.data?.error || 'Failed to request email change'
        console.error('Error requesting email change:', error)
    } finally {
        savingEmail.value = false
    }
}

async function saveAddresses() {
    savingAddresses.value = true
    addressMessage.value = ''

    try {
        const finalBillingAddress = useShippingForBilling.value ? shippingAddress.value : billingAddress.value
        const result = await userService.updateAddresses(shippingAddress.value, finalBillingAddress)

        if (result.success) {
            addressMessage.value = 'Addresses saved successfully!'
            setTimeout(() => {
                addressMessage.value = ''
            }, 3000)
        } else {
            addressMessage.value = result.error || 'Failed to save addresses'
        }
    } catch (error) {
        addressMessage.value = error.response?.data?.error || 'Failed to save addresses'
        console.error('Error saving addresses:', error)
    } finally {
        savingAddresses.value = false
    }
}

function closePasswordModal() {
    showPasswordModal.value = false
    currentPassword.value = ''
    newPassword.value = ''
    confirmNewPassword.value = ''
    passwordMessage.value = ''
}

async function handleChangePassword() {
    if (newPassword.value !== confirmNewPassword.value) {
        passwordMessage.value = 'New passwords do not match'
        return
    }

    if (newPassword.value.length < 8) {
        passwordMessage.value = 'New password must be at least 8 characters long'
        return
    }

    changingPassword.value = true
    passwordMessage.value = ''

    try {
        const result = await userService.changePassword(currentPassword.value, newPassword.value)

        if (result.success) {
            passwordMessage.value = 'Password changed successfully!'
            setTimeout(() => {
                closePasswordModal()
            }, 2000)
        } else {
            passwordMessage.value = result.error || 'Failed to change password'
        }
    } catch (error) {
        passwordMessage.value = error.response?.data?.error || 'Failed to change password'
        console.error('Error changing password:', error)
    } finally {
        changingPassword.value = false
    }
}

function closeDeleteModal() {
    showDeleteModal.value = false
    deleteConfirmation.value = ''
    deleteMessage.value = ''
}

async function openDeleteModal() {
    showDeleteModal.value = true
    await nextTick()
    const currentLang = translationService.getLanguage()
    if (currentLang !== 'en' && deleteModalRef.value) {
        translatingModal.value = true
        await translationService.translateElement(deleteModalRef.value)
        translatingModal.value = false
    }
}

async function confirmDeleteAccount() {
    if (deleteConfirmation.value !== 'DELETE') {
        deleteMessage.value = 'Please type DELETE to confirm'
        return
    }

    deletingAccount.value = true
    deleteMessage.value = ''

    try {
        const result = await userService.deleteAccount()

        if (result.success) {
            // Account deleted successfully, log out and redirect
            await authService.logout()
            window.location.href = '/'
        } else {
            deleteMessage.value = result.error || 'Failed to delete account'
        }
    } catch (error) {
        deleteMessage.value = error.response?.data?.error || 'Failed to delete account'
        console.error('Error deleting account:', error)
    } finally {
        deletingAccount.value = false
    }
}

function saveCookiePreferences() {
    savingCookies.value = true
    cookieMessage.value = ''

    try {
        updatePreferences(localCookiePreferences.value)
        cookieMessage.value = 'Cookie preferences saved successfully!'
        setTimeout(() => {
            cookieMessage.value = ''
        }, 3000)
    } catch (error) {
        cookieMessage.value = 'Failed to save cookie preferences'
        console.error('Error saving cookie preferences:', error)
    } finally {
        savingCookies.value = false
    }
}
</script>
